import React, { useCallback, useEffect, useState } from "react";

import CancelIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

import { ApplicationEndpoints } from "@generated/application";
import {
  ApiRequestError,
  apiFetch,
  apiPost,
  apiPut,
} from "@platform/http/apiClient";
import { Application } from "src/domains/application/types";

import {
  type FormService,
  formStateToYaml,
  manifestToFormState,
} from "./manifest-form-helpers";
import { ManifestFormEditor } from "./ManifestFormEditor";

// ── Types ────────────────────────────────────────────────────────────────────

interface ManifestPreviewTabProps {
  system: Application;
}

interface ManifestPreviewResponse {
  yaml: string;
  services?: { name: string; type: string }[];
}

interface RushaYmlResponse {
  content: string;
  sha: string;
  branch: string;
}

interface ValidationResponse {
  valid: boolean;
  errors?: { path: string; message: string }[];
  services?: { name: string; type: string }[];
}

interface CommitResponse {
  commitSha: string;
  message: string;
}

interface SyncResponse {
  commitSha: string;
  services: { name: string; type: string }[];
  manifestSourceSha: string;
}

// ── Component ────────────────────────────────────────────────────────────────

export function ManifestPreviewTab({
  system,
}: ManifestPreviewTabProps): React.ReactElement {
  const [yaml, setYaml] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [editTab, setEditTab] = useState<"code" | "form">("code");
  const [editContent, setEditContent] = useState("");
  const [formServices, setFormServices] = useState<FormService[]>([]);
  const [formPipelineMode, setFormPipelineMode] = useState("rusha_managed");
  const [parseError, setParseError] = useState<string | null>(null);
  const [fileSha, setFileSha] = useState<string | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    { path: string; message: string }[] | null
  >(null);
  const [commitDialogOpen, setCommitDialogOpen] = useState(false);
  const [commitMessage, setCommitMessage] = useState("");
  const [commitLoading, setCommitLoading] = useState(false);
  const [commitError, setCommitError] = useState<string | null>(null);
  const [commitSuccess, setCommitSuccess] = useState<string | null>(null);

  const alias = system.alias;

  const fetchManifest = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<ManifestPreviewResponse>(
        ApplicationEndpoints.GET_APPLICATION_MANIFEST.replace(":id", alias),
      );
      setYaml(res.yaml ?? JSON.stringify(res, null, 2));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load manifest");
    } finally {
      setLoading(false);
    }
  }, [alias]);

  useEffect(() => {
    void fetchManifest();
  }, [fetchManifest]);

  const handleCopy = () => {
    void navigator.clipboard.writeText(yaml);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  // ── Refresh: sync rusha.yml from GitHub default branch ─────────────────────

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      await apiPost<SyncResponse, Record<string, never>>(
        ApplicationEndpoints.SYNC_RUSHA_YML.replace(":id", alias),
        {},
      );
      // Sync succeeded — re-fetch the manifest from DB to display fresh YAML
      await fetchManifest();
    } catch (err) {
      if (err instanceof ApiRequestError) {
        if (err.status === 404) {
          setError("No rusha.yml found in the repository.");
        } else if (err.status === 400) {
          // Validation errors — show them but keep the cached manifest
          const errs = err.data?.errors;
          if (errs && errs.length > 0) {
            setError(
              `rusha.yml validation failed: ${errs.map((e) => `${e.path}: ${e.message}`).join("; ")}`,
            );
          } else {
            setError("rusha.yml validation failed.");
          }
        } else {
          setError(err.message || "Failed to sync rusha.yml from GitHub.");
        }
      } else {
        setError(
          err instanceof Error ? err.message : "Failed to sync rusha.yml.",
        );
      }
    } finally {
      setRefreshing(false);
    }
  }, [alias, fetchManifest]);

  // ── Edit mode ──────────────────────────────────────────────────────────────

  const handleEnterEdit = useCallback(async () => {
    setEditLoading(true);
    setEditError(null);
    setValidationErrors(null);
    setCommitSuccess(null);
    setParseError(null);
    try {
      const res = await apiFetch<RushaYmlResponse>(
        ApplicationEndpoints.GET_RUSHA_YML.replace(":id", alias),
      );
      setEditContent(res.content);
      setFileSha(res.sha);
      // Initialize form state from the loaded content
      const formState = manifestToFormState(res.content);
      if ("error" in formState) {
        setEditTab("code");
        setParseError(formState.error);
      } else {
        setFormServices(formState.services);
        setFormPipelineMode(formState.pipelineMode);
        setEditTab("code");
        setParseError(null);
      }
      setEditMode(true);
    } catch (err) {
      // 404: rusha.yml doesn't exist in repo — start from the cached manifest
      if (err instanceof ApiRequestError && err.status === 404) {
        setEditContent(yaml);
        setFileSha("");
        // Try to initialize form state from cached YAML
        const formState = manifestToFormState(yaml);
        if (!("error" in formState)) {
          setFormServices(formState.services);
          setFormPipelineMode(formState.pipelineMode);
        }
        setEditTab("code");
        setEditMode(true);
      } else {
        setEditError(
          err instanceof Error ? err.message : "Failed to load rusha.yml",
        );
      }
    } finally {
      setEditLoading(false);
    }
  }, [alias, yaml]);

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditContent("");
    setFormServices([]);
    setFormPipelineMode("rusha_managed");
    setParseError(null);
    setValidationErrors(null);
    setEditError(null);
    setCommitSuccess(null);
  };

  // ── Code ↔ Form tab switching ──────────────────────────────────────────────

  const handleSwitchToForm = useCallback(() => {
    const formState = manifestToFormState(editContent);
    if ("error" in formState) {
      setParseError(formState.error);
      return;
    }
    setFormServices(formState.services);
    setFormPipelineMode(formState.pipelineMode);
    setParseError(null);
    setEditTab("form");
  }, [editContent]);

  const handleSwitchToCode = useCallback(() => {
    const metadata = {
      name: system.alias,
      project: system.projectAlias ?? system.alias,
      org: system.organizationAlias ?? "",
    };
    const newYaml = formStateToYaml(formPipelineMode, formServices, metadata);
    setEditContent(newYaml);
    setEditTab("code");
  }, [formPipelineMode, formServices, system]);

  const handleValidate = useCallback(async () => {
    setEditLoading(true);
    setValidationErrors(null);
    setEditError(null);

    // If in form mode, serialize form state to YAML before validating
    let contentToValidate = editContent;
    if (editTab === "form") {
      const metadata = {
        name: system.alias,
        project: system.projectAlias ?? system.alias,
        org: system.organizationAlias ?? "",
      };
      contentToValidate = formStateToYaml(
        formPipelineMode,
        formServices,
        metadata,
      );
      // Also update editContent so Code mode shows the latest
      setEditContent(contentToValidate);
    }

    try {
      const res = await apiPost<ValidationResponse, { content: string }>(
        ApplicationEndpoints.VALIDATE_RUSHA_YML.replace(":id", alias),
        { content: contentToValidate },
      );
      if (res.valid) {
        setValidationErrors(null);
        setCommitDialogOpen(true);
        setCommitMessage("chore: update rusha.yml");
      } else {
        setValidationErrors(res.errors ?? []);
      }
    } catch (err) {
      setEditError(err instanceof Error ? err.message : "Validation failed");
    } finally {
      setEditLoading(false);
    }
  }, [alias, editContent, editTab, formPipelineMode, formServices, system]);

  const handleCommit = useCallback(async () => {
    setCommitLoading(true);
    setCommitError(null);

    // If in form mode, serialize form state to YAML before committing
    let contentToCommit = editContent;
    if (editTab === "form") {
      const metadata = {
        name: system.alias,
        project: system.projectAlias ?? system.alias,
        org: system.organizationAlias ?? "",
      };
      contentToCommit = formStateToYaml(
        formPipelineMode,
        formServices,
        metadata,
      );
    }

    try {
      const res = await apiPut<
        CommitResponse,
        {
          content: string;
          baseSha: string;
          message: string;
        }
      >(ApplicationEndpoints.UPDATE_RUSHA_YML.replace(":id", alias), {
        content: contentToCommit,
        baseSha: fileSha ?? "",
        message: commitMessage,
      });
      setCommitSuccess(
        `Committed rusha.yml (${res.commitSha.slice(0, 8)}). Push webhook will trigger a pipeline run.`,
      );
      setCommitDialogOpen(false);
      setEditMode(false);
      // Refresh the manifest display
      void fetchManifest();
    } catch (err) {
      if (err instanceof ApiRequestError) {
        if (err.status === 409) {
          setCommitError(
            "rusha.yml has been modified since you last loaded it. Refresh and try again.",
          );
        } else if (err.status === 400 || err.status === 422) {
          // Validation errors from the commit endpoint (400) or pipeline
          // trigger (422). The errors array may be under `errors` or
          // `validationErrors` depending on the source.
          const errors = err.data?.errors ?? err.data?.validationErrors;
          if (Array.isArray(errors) && errors.length > 0) {
            setCommitError(
              `rusha.yml validation failed: ${errors
                .map(
                  (e: { path: string; message: string }) =>
                    `${e.path}: ${e.message}`,
                )
                .join("; ")}`,
            );
          } else {
            setCommitError(err.message);
          }
        } else {
          setCommitError(err.message);
        }
      } else {
        setCommitError(err instanceof Error ? err.message : "Commit failed");
      }
    } finally {
      setCommitLoading(false);
    }
  }, [
    alias,
    editContent,
    editTab,
    formPipelineMode,
    formServices,
    system,
    fileSha,
    commitMessage,
    fetchManifest,
  ]);

  // ── Card wrapper matching mockup ───────────────────────────────────────────
  const cardSx = {
    bgcolor: "background.paper",
    border: "1px solid",
    borderColor: "divider",
    borderRadius: "12px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  };

  return (
    <Box sx={cardSx}>
      {/* Card header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          px: 2.5,
          py: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            color: "text.secondary",
          }}
        >
          rusha.yml
        </Typography>
        <Stack direction="row" spacing={1}>
          {editMode ? (
            <>
              <ToggleButtonGroup
                size="small"
                value={editTab}
                exclusive
                onChange={(_, value) => {
                  if (value === "form") handleSwitchToForm();
                  else if (value === "code") handleSwitchToCode();
                }}
                sx={{ mr: 1 }}
              >
                <ToggleButton
                  value="code"
                  sx={{
                    textTransform: "none",
                    fontSize: 11,
                    fontWeight: 600,
                    py: 0.25,
                    px: 1.5,
                  }}
                >
                  Code
                </ToggleButton>
                <ToggleButton
                  value="form"
                  sx={{
                    textTransform: "none",
                    fontSize: 11,
                    fontWeight: 600,
                    py: 0.25,
                    px: 1.5,
                  }}
                >
                  Form
                </ToggleButton>
              </ToggleButtonGroup>
              <Button
                size="small"
                startIcon={<CancelIcon sx={{ fontSize: 16 }} />}
                onClick={handleCancelEdit}
                disabled={editLoading || commitLoading}
                sx={{ textTransform: "none", fontSize: 12, fontWeight: 600 }}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<SaveIcon sx={{ fontSize: 16 }} />}
                onClick={() => void handleValidate()}
                disabled={editLoading || commitLoading}
                sx={{ textTransform: "none", fontSize: 12, fontWeight: 600 }}
              >
                {editLoading ? <CircularProgress size={14} /> : "Commit"}
              </Button>
            </>
          ) : (
            <>
              <Button
                size="small"
                startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
                onClick={() => void handleRefresh()}
                disabled={loading || refreshing}
                sx={{ textTransform: "none", fontSize: 12, fontWeight: 600 }}
              >
                {refreshing ? <CircularProgress size={14} /> : "Refresh"}
              </Button>
              <Button
                size="small"
                startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                onClick={() => void handleEnterEdit()}
                disabled={loading || editLoading}
                sx={{ textTransform: "none", fontSize: 12, fontWeight: 600 }}
              >
                {editLoading ? <CircularProgress size={14} /> : "Edit"}
              </Button>
              <Button
                size="small"
                startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
                onClick={handleCopy}
                disabled={loading || !yaml}
                sx={{ textTransform: "none", fontSize: 12, fontWeight: 600 }}
              >
                {copied ? "Copied ✓" : "Copy"}
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      {/* Success banner */}
      {commitSuccess && (
        <Box sx={{ px: 2.5, pt: 2 }}>
          <Alert
            severity="success"
            onClose={() => setCommitSuccess(null)}
            sx={{ fontSize: 12 }}
          >
            {commitSuccess}
          </Alert>
        </Box>
      )}

      {/* Edit mode error */}
      {editError && (
        <Box sx={{ px: 2.5, pt: 2 }}>
          <Alert
            severity="error"
            onClose={() => setEditError(null)}
            sx={{ fontSize: 12 }}
          >
            {editError}
          </Alert>
        </Box>
      )}

      {/* Validation errors */}
      {validationErrors && validationErrors.length > 0 && (
        <Box sx={{ px: 2.5, pt: 2 }}>
          <Alert severity="warning" sx={{ fontSize: 12 }}>
            <Typography sx={{ fontWeight: 600, fontSize: 12, mb: 0.5 }}>
              rusha.yml validation failed:
            </Typography>
            <Stack spacing={0.5}>
              {validationErrors.map((e, i) => (
                <Typography key={i} sx={{ fontSize: 11 }}>
                  {e.path}: {e.message}
                </Typography>
              ))}
            </Stack>
          </Alert>
        </Box>
      )}

      {/* Content */}
      {loading ? (
        <Box sx={{ p: 2.5, display: "flex", justifyContent: "center" }}>
          <CircularProgress size={24} />
        </Box>
      ) : error ? (
        <Box sx={{ p: 2.5 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      ) : editMode ? (
        <Box
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          {parseError && (
            <Box sx={{ p: 2.5, pb: 0 }}>
              <Alert
                severity="warning"
                onClose={() => setParseError(null)}
                sx={{ fontSize: 12 }}
              >
                Cannot switch to Form mode: {parseError}. Fix the YAML in Code
                mode first.
              </Alert>
            </Box>
          )}
          {editTab === "form" ? (
            <Box
              sx={{
                maxHeight: 600,
                overflowY: "auto",
              }}
            >
              <ManifestFormEditor
                services={formServices}
                pipelineMode={formPipelineMode}
                onChange={setFormServices}
                onPipelineModeChange={setFormPipelineMode}
              />
            </Box>
          ) : (
            <Box
              component="textarea"
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              spellCheck={false}
              sx={{
                width: "100%",
                minHeight: 400,
                m: 0,
                p: 2.5,
                bgcolor: "rgba(10,20,36,0.4)",
                border: "none",
                outline: "none",
                fontFamily: "monospace",
                fontSize: 12,
                lineHeight: 1.6,
                color: "text.primary",
                resize: "vertical",
                "&:focus": {
                  outline: "2px solid",
                  outlineColor: "primary.main",
                  outlineOffset: "-2px",
                },
              }}
            />
          )}
        </Box>
      ) : (
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 2.5,
            bgcolor: "rgba(10,20,36,0.4)",
            overflow: "auto",
            fontFamily: "monospace",
            fontSize: 12,
            lineHeight: 1.6,
            color: "text.secondary",
            borderBottom: "1px solid",
            borderColor: "divider",
            borderRadius: 0,
          }}
        >
          {yaml || "No manifest available."}
        </Box>
      )}

      {/* Commit dialog */}
      <Dialog
        open={commitDialogOpen}
        onClose={() => !commitLoading && setCommitDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 14, fontWeight: 700 }}>
          Commit rusha.yml
        </DialogTitle>
        <DialogContent>
          {commitError && (
            <Alert severity="error" sx={{ mb: 2, fontSize: 12 }}>
              {commitError}
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            label="Commit message"
            value={commitMessage}
            onChange={(e) => setCommitMessage(e.target.value)}
            disabled={commitLoading}
            size="small"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCommitDialogOpen(false)}
            disabled={commitLoading}
            size="small"
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleCommit()}
            disabled={commitLoading || !commitMessage.trim()}
            size="small"
            startIcon={
              commitLoading ? (
                <CircularProgress size={14} />
              ) : (
                <SaveIcon sx={{ fontSize: 16 }} />
              )
            }
            sx={{ textTransform: "none" }}
          >
            Commit & Push
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ManifestPreviewTab;
